import { Link, useParams } from 'react-router-dom';
import { useCompanion } from '@/lib/data/companions';
import { useCompanionServices, bookingTypeLabel, unitLabel } from '@/lib/data/services';
import { useWallet } from '@/lib/data/wallet';
import { BookingTypeIcon } from '@/components/companion/ServiceIcon';
import BookPanel from '@/components/booking/BookPanel';
import {
  Avatar,
  Badge,
  Button,
  Card,
  PresenceDot,
  Rating,
  VerifiedMark,
} from '@/components/ui';

export default function CompanionProfile() {
  const { id } = useParams();
  const { companion: c, loading } = useCompanion(id);
  const { services, loading: loadingServices } = useCompanionServices(id);
  const { wallet } = useWallet();

  if (loading) {
    return (
      <div className="py-20 text-center">
        <p className="text-sm text-[var(--muted)]">Loading…</p>
      </div>
    );
  }

  if (!c) {
    return (
      <Card className="mx-auto max-w-md px-6 py-16 text-center">
        <h1 className="text-2xl font-semibold">We could not find that person</h1>
        <p className="mt-2 text-sm text-[var(--ink-2)]">They may have taken their profile down.</p>
        <div className="mt-6">
          <Button to="/companions" variant="secondary">
            Back to everyone
          </Button>
        </div>
      </Card>
    );
  }

  const available = c.isOnline && !c.isBusy;

  return (
    <div className="animate-rise">
      <Link
        to="/companions"
        className="group inline-flex items-center gap-1.5 text-sm text-[var(--ink-2)] transition-colors hover:text-[var(--ink)]"
      >
        <svg
          viewBox="0 0 16 16"
          className="size-3.5 transition-transform duration-200 group-hover:-translate-x-1"
          aria-hidden
        >
          <path
            d="M13 8H4M7.5 4.5L4 8l3.5 3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Everyone
      </Link>

      <div className="mt-6 grid gap-8 lg:grid-cols-[1fr_350px]">
        {/* ═══ Main ═════════════════════════════════════════════════════════ */}
        <div>
          <div className="flex flex-wrap items-start gap-5">
            <Avatar
              initials={c.avatarInitials}
              hue={c.avatarHue}
              size={88}
              active={available}
              // Their uploaded photo wins. The companion-<id>.jpg fallback is
              // for hand-placed demo portraits and resolves to nothing for a
              // real account, at which point the generated avatar shows.
              photo={c.avatarUrl ?? `companion-${c.id}.jpg`}
              photoPosition={c.photoPosition}
              alt={c.name}
            />

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-semibold">{c.name}</h1>
                {c.verified && <VerifiedMark size={20} />}
              </div>
              <p className="mt-1 text-lg text-[var(--ink-2)]">{c.headline}</p>
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
                <PresenceDot isOnline={c.isOnline} isBusy={c.isBusy} />
                <Rating value={c.rating} count={c.ratingCount} />
                {c.totalMinutes > 0 && (
                  <span className="text-sm text-[var(--muted)] tabular-nums">
                    {Math.round(c.totalMinutes / 60).toLocaleString('en-IN')} hours booked
                  </span>
                )}
              </div>
            </div>
          </div>

          <Card className="mt-8 p-6">
            <p className="eyebrow">In their words</p>
            <blockquote className="mt-3 font-display text-xl leading-relaxed">
              “{c.bio}”
            </blockquote>
          </Card>

          {/* What they offer, and what it costs.
              This reads from companion_services — the same rows the companion
              edits at /companion/services. Before this the page showed the old
              chat/audio/video columns instead, so anything a companion
              configured was invisible to the people meant to book it. */}
          <section className="mt-8">
            <p className="eyebrow mb-3">What they offer</p>

            {loadingServices ? (
              <div
                className="h-28 animate-pulse rounded-2xl"
                style={{ background: 'var(--surface-2)' }}
              />
            ) : services.length === 0 ? (
              <Card className="px-5 py-8 text-center">
                <p className="text-sm text-[var(--ink-2)]">
                  They have not listed any services yet.
                </p>
              </Card>
            ) : (
              <ul className="flex flex-col gap-3">
                {services.map((s) => (
                  <Card as="li" key={s.id} className="p-5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-semibold">{s.categoryName}</h3>
                        {s.headline && (
                          <p className="mt-1 text-sm text-[var(--ink-2)]">{s.headline}</p>
                        )}
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      {s.rates.map((r) => (
                        <span
                          key={r.bookingType}
                          className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm"
                          style={{ background: 'var(--surface-2)' }}
                        >
                          <BookingTypeIcon type={r.bookingType} size={14} />
                          <span className="text-[var(--ink-2)]">
                            {bookingTypeLabel(r.bookingType)}
                          </span>
                          <span className="font-semibold tabular-nums">
                            {r.priceCoins}
                            <span className="font-normal text-[var(--ink-2)]">
                              {unitLabel(r.unit)}
                            </span>
                          </span>
                        </span>
                      ))}
                    </div>
                  </Card>
                ))}
              </ul>
            )}
          </section>

          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <Card className="p-6 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[var(--shadow-md)]">
              <p className="eyebrow">Speaks</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {c.languages.map((l) => (
                  <Badge key={l} tone="teal">
                    {l}
                  </Badge>
                ))}
              </div>
            </Card>

            <Card className="p-6 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[var(--shadow-md)]">
              <p className="eyebrow">Based in</p>
              <p className="mt-3 text-lg font-medium">{c.city || 'Not set'}</p>
              <p className="mt-2 text-xs text-[var(--muted)]">
                Only matters for meeting in person — online services work anywhere.
              </p>
            </Card>
          </div>
        </div>

        {/* ═══ Booking ══════════════════════════════════════════════════════ */}
        <div className="lg:sticky lg:top-28 lg:self-start">
          <BookPanel
            services={services}
            wallet={wallet}
            available={available}
            isBusy={c.isBusy}
          />
        </div>
      </div>
    </div>
  );
}
